import { useHeaderFilterMenu } from "@/shared/hooks/useHeaderFilterMenu";
import { Portfolio } from "@/types/Portfolio";
import { NewsFeedItem } from "@/shared/api/firehose/models";
import { mapDataWithMatchingPortfolios } from "@/utils/portfolioUtils";
import { firehosePortfolioMatcher } from "../../firehose-all/utils";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useMemo, useState, createElement } from "react";
import { useFirehoseSources } from "./useFirehoseSources";
import { FilterMode } from "@/shared/models/filters";
import {
  FilterModeSelector,
  ClearSelectionsFooter,
} from "@/shared/components/FilterComponents";
import { isPrebuiltWatchlist } from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import {
  groupWatchlistsByCategory,
  NEWSQUAWK_CATEGORIES,
  NewsquawkCategory,
} from "@/pages/PortfolioBuilder/newsquawkCategories";

interface FirehoseFilterOption {
  setActions?: (actions: React.ReactElement[]) => void;
  pushAction?: (action: React.ReactElement) => void;
  persistKey?: string;
  portfolios: Portfolio[];
  sources?: string[];
}

export type { FilterMode };

const HIDE_FILTER_MODE_SELECTOR = true; // Temporarily hide filter mode selector for Firehose widgets

// Helper to check if a portfolio is ticker-based (has tickers, no keywords)
const isTickerWatchlist = (p: Portfolio): boolean => {
  return p.tickers.length > 0 && (!p.keywords || p.keywords.length === 0);
};

// Helper to check if a portfolio is keyword-based (has keywords, no tickers)
const isKeywordWatchlist = (p: Portfolio): boolean => {
  return (p.keywords?.length ?? 0) > 0 && p.tickers.length === 0;
};

/**
 * @deprecated Use useFirehoseFilterPanel and useFirehoseHighlightPanel instead.
 * This hook provides a combined filter+highlight menu (old approach).
 */
export const useFirehoseHeaderFilterMenu = (options: FirehoseFilterOption) => {
  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();
  const firehoseSources = useFirehoseSources();
  const [filterMode, setFilterMode] = useState<FilterMode>("AND");

  // Define static filterConfig with column layout, collapsible sections, and footers
  const filterConfig = useMemo(() => {
    // Create filter mode selector for the Filter section
    const filterModeFooter = createElement(FilterModeSelector, {
      filterMode,
      onFilterModeChange: setFilterMode,
    });

    // Separate user and prebuilt watchlists
    const userWatchlists = options.portfolios.filter((p) => !isPrebuiltWatchlist(p));
    const prebuiltWatchlists = options.portfolios.filter((p) => isPrebuiltWatchlist(p));

    // Split user watchlists by type
    const userTickerWatchlists = userWatchlists.filter(isTickerWatchlist);
    const userKeywordWatchlists = userWatchlists.filter(isKeywordWatchlist);
    const userMixedWatchlists = userWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Split prebuilt watchlists by type
    const prebuiltTickerWatchlists = prebuiltWatchlists.filter(isTickerWatchlist);
    const prebuiltKeywordWatchlists = prebuiltWatchlists.filter(isKeywordWatchlist);
    const prebuiltMixedWatchlists = prebuiltWatchlists.filter(
      (p) => p.tickers.length > 0 && (p.keywords?.length ?? 0) > 0
    );

    // Group prebuilt keyword watchlists by category
    const groupedKeywordWatchlists = groupWatchlistsByCategory(prebuiltKeywordWatchlists);

    // All ticker watchlist options (user + prebuilt)
    const allTickerWatchlists = [
      ...userTickerWatchlists,
      ...userMixedWatchlists,
      ...prebuiltTickerWatchlists,
      ...prebuiltMixedWatchlists,
    ];
    const tickerWatchlistOptions = allTickerWatchlists.map((portfolio) => ({
      id: String(portfolio.id),
      label: portfolio.name,
    }));

    // Build keyword watchlist options with category headers
    const keywordWatchlistOptions: { id: string; label: string; isHeader?: boolean }[] = [];
    for (const portfolio of userKeywordWatchlists) {
      keywordWatchlistOptions.push({
        id: String(portfolio.id),
        label: portfolio.name,
      });
    }
    for (const categoryConfig of NEWSQUAWK_CATEGORIES) {
      const categoryWatchlists = groupedKeywordWatchlists[categoryConfig.id as NewsquawkCategory];
      if (categoryWatchlists && categoryWatchlists.length > 0) {
        keywordWatchlistOptions.push({
          id: `header-${categoryConfig.id.toLowerCase().replace(/\s+/g, "-")}`,
          label: categoryConfig.label,
          isHeader: true,
        });
        for (const portfolio of categoryWatchlists) {
          keywordWatchlistOptions.push({
            id: String(portfolio.id),
            label: portfolio.name,
          });
        }
      }
    }

    return [
      // Filter sections - Ticker Watchlists
      {
        id: "ticker-watchlists-filter",
        label: "Filter by Ticker Watchlists",
        filterOptions: tickerWatchlistOptions,
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        footer: HIDE_FILTER_MODE_SELECTOR ? null : filterModeFooter,
      },
      // Filter sections - Keyword Watchlists
      {
        id: "keyword-watchlists-filter",
        label: "Filter by Keyword Watchlists",
        filterOptions: keywordWatchlistOptions,
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
      ...(options.sources?.includes("allStories")
        ? [
            {
              id: "sources-filter",
              label: "Filter by Source",
              filterOptions: firehoseSources.map((source) => ({
                id: String(source.source),
                label: source.label,
              })),
              columnIndex: 0,
              collapsible: true,
              showCount: true,
              defaultCollapsed: true,
            },
          ]
        : []),
      // Highlight sections - Ticker Watchlists
      {
        id: "ticker-watchlists-highlight",
        label: "Highlight by Ticker Watchlists",
        filterOptions: tickerWatchlistOptions,
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
      // Highlight sections - Keyword Watchlists
      {
        id: "keyword-watchlists-highlight",
        label: "Highlight by Keyword Watchlists",
        filterOptions: keywordWatchlistOptions,
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
      },
      ...(options.sources?.includes("allStories")
        ? [
            {
              id: "sources-highlight",
              label: "Highlight by Source",
              filterOptions: firehoseSources.map((source) => ({
                id: String(source.source),
                label: source.label,
              })),
              columnIndex: 0,
              collapsible: true,
              showCount: true,
              defaultCollapsed: true,
            },
          ]
        : []),
      {
        id: "category-filter",
        label: "Advanced Filters",
        description:
          "Filters with categories only are currently not supported for Firehose widgets",
        columnIndex: 0,
        collapsible: true,
        showCount: true,
        defaultCollapsed: true,
        filterOptions:
          categoryQueries.data
            ?.filter(
              (query) => query.portfolioIds?.length || query.searchKeyword
            )
            .map((query) => ({
              id: String(query.id),
              label: query.queryName,
            })) || [],
      },
    ];
  }, [
    options.portfolios,
    firehoseSources,
    options.sources,
    categoryQueries.data,
    filterMode,
  ]);

  // Create global footer component with clear selections button
  const globalFooter = useMemo(
    () => createElement(ClearSelectionsFooter, {}),
    []
  );

  const [filterSelections, setFilterSelections] = useHeaderFilterMenu(
    filterConfig,
    options.pushAction,
    options.persistKey,
    undefined,
    globalFooter
  );

  return [filterSelections, setFilterSelections, filterMode] as const;
};

export const applyFilters = (
  firehoseItems: NewsFeedItem[],
  filterSelections: Record<string, string[]>,
  portfolios: Portfolio[] // portfolios from API now include both user and prebuilt
) => {
  // Combine highlight selections from both ticker and keyword watchlist sections
  const highlightSelections = [
    ...(filterSelections["ticker-watchlists-highlight"] || []),
    ...(filterSelections["keyword-watchlists-highlight"] || []),
  ];

  const portfoliosToHighlight = portfolios.filter((portfolio) =>
    highlightSelections.includes(String(portfolio.id))
  );

  return mapDataWithMatchingPortfolios(
    firehoseItems,
    [],
    portfoliosToHighlight,
    firehosePortfolioMatcher
  );
};
